import styled from 'styled-components/macro';
import {useState} from "react";
import {
    Button,
    Checkbox,
    FormControl,
    FormHelperText,
    FormLabel,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useToast
} from "@chakra-ui/react";
import {Select as MultiSelect} from "chakra-react-select";
import {useHistory} from "react-router-dom";
import {useFeedback} from "../store/feedback";
import {useCommunitySettings, useSaveSettings, useSaveStylesheet} from "../api/communities";
import {useCommunityDesignToken, useSetCommunityDesign} from "../api/create_image";
import {themecolors} from "../utils/themecolors";

const LogoUpload = styled.div``;
const HeaderUpload = styled.div``;
const UploadText = styled.div``;
const Topics = styled.div``;

const OptionType = styled.div`
  display: flex;
  cursor: pointer;
  margin-bottom: 24px;
`;
const OptionText = styled.div`
  margin-left: 24px;
  display: flex;
  flex-direction: column;
   h2 {
     font-size: 17px !important;
     margin: 0 !important;
   }
  p {
    font-size: 15px;
  }
`;

const title = {
    1: 'Setting Up',
    2: 'Style your community',
    3: 'Community Topics',
    4: 'Community Safety',
    5: 'All done'
}

const topics = [
    "Politics",
    "NSFW",
    "Memes",
    "Culture",
    "Sports",
    "Gaming",
    "Guns",
    "Hobbies",
    "Cars",
    "Videos",
    "Stories",
    "Technology",
    "Streaming",
    "Discussion",
    "Entertainment",
    "Music",
    "Movies",
    "TV",
    "Humor",
    "GIFs",
    "Lifestyle",
    "News",
    "Places",
    "Finance"
]

const CommunityOnboarding = styled(({className, onClose, community}) => {
    const toast = useToast();
    const history = useHistory();
    const {error, feedbackCh} = useFeedback();
    const [logo, setLogo] = useState(null);
    const [header, setHeader] = useState(null);
    const [settings, setSettings] = useState({
            appSafe: true,
            description: '',
            topics: [],
            nsfw: false,
        }
    );
    useCommunitySettings(feedbackCh, community, setSettings);
    const set = (name) => (value) => setSettings((setting) => ({...setting, [name]: value}));
    const [posting, setPosting] = useState(false);

    const [page, setPage] = useState(1);

    const saveSettings = useSaveSettings(feedbackCh);
    const saveStylesheet = useSaveStylesheet(feedbackCh);
    const uploadToken = useCommunityDesignToken(feedbackCh);
    const setCommunityDesign = useSetCommunityDesign(feedbackCh);
    const handleCreate = async () => {
        setPosting(true);
        try {
            await saveSettings(community, settings);
            if (logo || header) {
                const token = await uploadToken(community, header ? true : false, logo ? true : false);
                await setCommunityDesign(header, logo, token)
            }
            await saveStylesheet(community);
            toast({
                title: `c/${community} has been created!`,
                status: 'success',
                isClosable: true,
            });
            setPage(5);
            history.push(`/c/${community}`);
        } catch (err) {
            setPage(1);
        }
        setPosting(false);
    };

    const handlePaginate = async () => {
        if (page < 4) {
            setPage(page + 1);
        } else if (page === 4) {
            await handleCreate();
        } else {
            onClose();
        }
    };

    return (
        <>
            <Modal
                isOpen
                size={'xl'}
            >
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>{title[page]}</ModalHeader>
                    <ModalBody pb={6} className={className}>

                        {page === 1 &&
                            <div>
                                <p style={{paddingBottom: 12}}>
                                    Your new community has been created.
                                </p>
                                <p>
                                    Next, we need you to configure some basic settings. All of these settings can be changed in the future.
                                </p>
                            </div>
                        }

                        {page === 2 &&
                            <FormControl>
                                <p style={{paddingBottom: 12}}>Community Logo and Header will apply to desktop and mobile interfaces. Setting these is important to make your community stand out.</p>
                                <p style={{paddingBottom: 24}}>If you're not ready to configure these, you can skip this step.</p>
                                <LogoUpload>
                                    <UploadText>
                                        <FormLabel>Upload Community Logo</FormLabel>
                                        <FormHelperText>Recommended size: 512x512px</FormHelperText>
                                        <input type={"file"} onChange = {(e) => setLogo(e.target.files[0])}/>
                                    </UploadText>
                                </LogoUpload>
                                <HeaderUpload>
                                    <UploadText>
                                        <FormLabel>Upload Community Header</FormLabel>
                                        <FormHelperText>Recommended size: 1920x1080px</FormHelperText>
                                        <input type={"file"} onChange = {(e) => setHeader(e.target.files[0])}/>
                                    </UploadText>
                                </HeaderUpload>
                            </FormControl>
                        }

                        {page === 3 &&
                            <FormControl>

                                <Topics>
                                    <p style={{paddingBottom: 12}}>Select topics that match your community, which helps users discover it.</p>
                                    <p>If your community allows discussion of politics or social issues in any capacity, "Politics" must be selected.</p>
                                </Topics>

                                <MultiSelect
                                    isMulti
                                    name="topics"
                                    value={settings.topics}
                                    options={topics}
                                    getOptionLabel={option => option}
                                    getOptionValue={option => option}
                                    placeholder="Select topics..."
                                    closeMenuOnSelect={false}
                                    onChange={set('topics')}
                                />


                            </FormControl>
                        }

                        {page === 4 &&
                            <>
                                <OptionType onClick={() => set('appSafe')(!settings.appSafe)}>
                                    <Checkbox size='lg' isChecked={settings.appSafe}
                                              onChange={(e) => set('appSafe')(e.target.checked)}/>

                                    <OptionText>
                                        <h2>Safe for App Stores</h2>
                                        <p style={{fontSize: '15px'}}>If your community is excessively edgy, you must not select this. Your community will not be available in our app, due to app store policies.</p>
                                    </OptionText>

                                </OptionType>

                                <OptionType onClick={() => set('nsfw')(!settings.nsfw)}>
                                    <Checkbox size='lg' isChecked={!settings.nsfw}
                                              onChange={(e) => set('nsfw')(!e.target.checked)}/>

                                    <OptionText>
                                        <h2>Safe for Work</h2>
                                        <p style={{fontSize: '15px'}}>Select this if your community is appropriate for a working environment.</p>
                                    </OptionText>

                                </OptionType>
                            </>
                        }

                        {page === 5 &&
                        <>
                            <p style={{paddingBottom: 16}}>The basics of your community are now configured.</p>
                            <p>Next, we would recommend that you read our <a target="_blank" href={"https://help.scored.co/knowledge-base/create-a-community/"}>'Create a Community' series of help articles</a> to learn about more advanced customization, as well as advice on growing your community.</p>
                        </>
                        }

                    </ModalBody>

                    <ModalFooter>
                        <Button variant="blue" isLoading={posting} onClick={handlePaginate}>
                            {page < 5 ? 'Next' : 'Done'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    )
})`
  font-size: 16px;
  ${HeaderUpload} {
    margin-top: 24px;
  }
  ${HeaderUpload} ${UploadText} {
    margin-top: 24px;
  }
  ${LogoUpload} {
    display: flex;
    align-items: center;
  }
  ${Topics} {
    margin-bottom: 18px;
  }
  input[type="file"] {
    margin-top: 12px;
  }
  a {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.pure_white : theme.placeholder_link_2};
    font-weight: ${({theme}) => theme.color.name === 'dark' ? '500' : 'inherit'};
  }
`;

export default CommunityOnboarding;